import { Color } from '@coast/schemas';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { getTagOption } from '@shared/components/blocks/lib/tags';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { TagList } from '@shared/components/blocks/lib/tags/tagList';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMultiSelectModal } from '@shared/components/multiSelect';
import { MBTextBody1 } from '@shared/components/typography';
import { getAnchorName } from '@shared/styles';
import { Trigger } from '@shared/util/hooks';
import { useWorkflowTagOptions } from '@shared/util/hooks/useWorkflowTagOptions';
import { i18n } from '@shared/util/i18n';

interface Props {
  fieldName: string;
  label: string;
  max: number;
  trigger: Trigger;
  disabled: boolean;
  onChange(selectedTags: string[]): void;
}

export function MBCardInputBlockQuestionSingleSelect({
  fieldName,
  label,
  max = 1,
  trigger,
  disabled,
  onChange,
}: Props) {
  const { values } = useCardFormContext();
  const value = values[fieldName];
  const tagValues = useMemo<string[]>(() => value || [], [value]);
  const { options, loading } = useWorkflowTagOptions(fieldName);
  const optionValues = useMemo(() => options.map(opt => opt.value), [options]);

  const labelExtractor = useCallback((val: string) => getTagOption(val, options)?.label, [options]);

  const match = useCallback(
    (val: string, query: string, exact: boolean) => {
      const extractedLabel = labelExtractor(val);
      if (isNil(extractedLabel)) {
        return false;
      }
      if (exact) {
        return extractedLabel.toLowerCase() === query.toLowerCase();
      }
      return extractedLabel.toLowerCase().includes(query.toLowerCase());
    },
    [labelExtractor],
  );

  const renderItem = useCallback(
    (val: string) => {
      const option = getTagOption(val, options);
      if (isNil(option)) {
        return null;
      }
      return (
        <View row centerVertically flex>
          <Tag
            color={option.backgroundColor as Color}
            label={option.label}
            archived={option.archived}
          />
        </View>
      );
    },
    [options],
  );

  const content = loading ? (
    <MBLoadingIndicator />
  ) : (
    Boolean(tagValues?.length) && (
      <View row wrap shrinkWrap style={{ maxWidth: '100%' }} paddingBottom={1}>
        <TagList fieldName={fieldName} tagValues={tagValues} />
      </View>
    )
  );

  const anchorName = getAnchorName('input-question-single-select', fieldName);

  return (
    <>
      <View paddingBottom={2}>
        <MBTextBody1 flex>{label}</MBTextBody1>
      </View>
      <View row paddingBottom={1}>
        {disabled ? (
          content
        ) : (
          <MBMultiSelectModal<string>
            label={i18n('card.blocks.question.answerButtonLabel')}
            anchorName={anchorName}
            onChange={onChange}
            max={max}
            renderItem={renderItem}
            values={optionValues}
            selectedValues={tagValues}
            labelExtractor={labelExtractor}
            match={match}
            archivedValues={[]}
            itemLabel={i18n('card.blocks.tagsInput.itemLabel')}
            itemLabelPlural={i18n('card.blocks.tagsInput.itemLabelPlural')}
            errorLabel={i18n('errors.tag.enterName')}
            itemsEditable={false}
            trigger={trigger}
          >
            <View style={{ anchorName }}>{content}</View>
          </MBMultiSelectModal>
        )}
      </View>
    </>
  );
}
